import { cleanStyle, sameStyle, type Style } from '../model';

/**
 * A layer: made and styled in the Layers panel, stored with the drawing, and only used by
 * its script, which puts shapes on it.
 */
export interface ScriptLayer {
  readonly name: string;
  readonly style: Style;
  readonly visible: boolean;
  readonly locked: boolean;
}

export type ScriptLayers = readonly ScriptLayer[];

/** DXF's default layer: always first, never renamed or deleted, and where shapes start. */
export const BASE_LAYER = '0';
export const MAX_LAYERS = 100;
const MAX_NAME = 64;
const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;
// Names the sandbox's `layers` object answers itself, or that JSON reads.
const RESERVED = new Set(['toJSON', '__proto__']);

const base = (): ScriptLayer => ({ name: BASE_LAYER, style: {}, visible: true, locked: false });

/** Why `name` cannot name the layer at `index`, or null when it can. */
export function layerNameProblem(name: string, layers: ScriptLayers, index = -1): string | null {
  if (name === '') return 'A layer needs a name';
  if (name === BASE_LAYER) return `Layer ${BASE_LAYER} always exists`;
  if (!IDENTIFIER.test(name) || name.length > MAX_NAME || RESERVED.has(name)) {
    return `"${name}" cannot be read as layers.${name}; use letters, digits, _ and $, not starting with a digit`;
  }
  const taken = layers.findIndex((layer) => layer.name === name);
  if (taken >= 0 && taken !== index) return `There is already a layer named "${name}"`;
  return null;
}

/** The layers with layer `0` first, adding it when missing, as on drawings saved before layers. */
export function withBaseLayer(layers: ScriptLayers): ScriptLayers {
  return layers[0]?.name === BASE_LAYER ? layers : [base(), ...layers];
}

/** Keeps only well-formed layers with unique names, from whatever a client sent. */
export function cleanLayers(value: unknown): ScriptLayers {
  const layers: ScriptLayer[] = [];
  for (const entry of Array.isArray(value) ? (value as unknown[]) : []) {
    if (layers.length === MAX_LAYERS) break;
    if (typeof entry !== 'object' || entry === null) continue;
    const { name, style, visible, locked } = entry as Record<string, unknown>;
    if (typeof name !== 'string') continue;
    if (name === BASE_LAYER ? layers.length > 0 : layerNameProblem(name, layers) !== null) continue;
    let clean: Style = {};
    try {
      clean = style === undefined ? {} : cleanStyle(style);
    } catch {
      // A style it cannot draw is dropped, keeping the layer.
    }
    layers.push({ name, style: clean, visible: visible !== false, locked: locked === true });
  }
  return withBaseLayer(layers);
}

export function sameLayers(a: ScriptLayers, b: ScriptLayers): boolean {
  return (
    a.length === b.length &&
    a.every((layer, i) => {
      const other = b[i]!;
      return (
        layer.name === other.name &&
        layer.visible === other.visible &&
        layer.locked === other.locked &&
        sameStyle(layer.style, other.style)
      );
    })
  );
}

/** The layers with the one at `from` moved to `to`; layer `0` stays first, so neither may be 0. */
export function moveLayer(layers: ScriptLayers, from: number, to: number): ScriptLayers {
  const valid = (index: number) => index > 0 && index < layers.length;
  if (from === to || !valid(from) || !valid(to)) return layers;
  const next = [...layers];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
}

/** The layers in the order named, bottom first; layer `0` stays at the bottom, named or not. */
export function orderLayers(layers: ScriptLayers, names: readonly string[]): ScriptLayers {
  const all = withBaseLayer(layers);
  const byName = new Map(all.map((layer) => [layer.name, layer]));
  const listed = names[0] === BASE_LAYER ? names.slice(1) : names;
  const ordered = [all[0]!];
  for (const name of listed) {
    if (name === BASE_LAYER) throw new Error(`Layer ${BASE_LAYER} is always at the bottom`);
    const layer = byName.get(name);
    if (!layer) {
      throw new Error(
        ordered.some((each) => each.name === name)
          ? `The order names layer "${name}" twice`
          : `There is no layer "${name}" to order`,
      );
    }
    byName.delete(name);
    ordered.push(layer);
  }
  byName.delete(BASE_LAYER);
  const missing = byName.keys().next();
  if (!missing.done) throw new Error(`The order leaves out layer "${missing.value}"`);
  return ordered;
}

/** The first of `layer1`, `layer2`, ... that no layer uses yet. */
export function nextLayerName(layers: ScriptLayers): string {
  for (let n = layers.length; ; n += 1) {
    const name = `layer${n}`;
    if (n > 0 && !layers.some((layer) => layer.name === name)) return name;
  }
}

/** Declarations for the code editor, so `layers.` completes the drawing's own names. */
export function layerTypes(layers: ScriptLayers): string {
  const members = withBaseLayer(layers).map(({ name }) =>
    name === BASE_LAYER ? `  readonly '${name}': Layer;` : `  readonly ${name}: Layer;`,
  );
  return `/** The drawing's layers, made and styled in the Layers panel; put a shape on one with .on(). */
declare const layers: {
${members.join('\n')}
};
`;
}
